import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { api } from '@/App';
import { Button } from '@/components/ui/button';
import { TrendingUp, MapPin, Building2 } from 'lucide-react';
import { formatCurrency, formatCurrencyShort } from '@/utils/currency';

const CITIES = [
  { id: 'all', name: 'All Cities' },
  { id: 'delhi', name: 'Delhi' },
  { id: 'gurgaon', name: 'Gurgaon' },
  { id: 'bangalore', name: 'Bangalore' },
  { id: 'noida', name: 'Noida' },
  { id: 'mumbai', name: 'Mumbai' }
];

export default function Properties() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [properties, setProperties] = useState([]);
  const [filteredProperties, setFilteredProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCity, setSelectedCity] = useState(searchParams.get('city') || 'all');
  const [cityCounts, setCityCounts] = useState({});

  useEffect(() => {
    const fetchProperties = async () => {
      try {
        // Use SSoT-compliant API - only show open investment opportunities
        const response = await api.get('/projects/opportunities');
        // Transform projects to properties format
        const transformedProperties = response.data.map(p => ({
          id: p.projectId,
          title: p.name,
          location: typeof p.location === 'object' ? `${p.location.city}, ${p.location.state}` : p.location || 'Location TBD',
          city: typeof p.location === 'object' ? p.location.city?.toLowerCase() : '',
          image_url: p.images?.[0] || 'https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?w=1200',
          property_type: p.type?.charAt(0).toUpperCase() + p.type?.slice(1) || 'Property',
          total_value: p.totalValue || p.financial?.unitValue || 0,
          min_investment: p.minInvestment || p.financial?.minInvestment || 500000,
          price_per_share: p.minInvestment || p.financial?.minInvestment || 500000,
          annual_return: parseInt(p.expectedReturns?.match(/\d+/)?.[0]) || 12,
          funded_percentage: p.financial?.fundingPercentage || 0,
          construction_progress: p.constructionProgress || 65,
          is_off_plan: p.type === 'residential',
          available_shares: Math.floor((p.totalValue || 0) / (p.minInvestment || 500000)) || 100,
          total_shares: Math.floor((p.totalValue || 0) / (p.minInvestment || 500000)) || 100
        }));
        setProperties(transformedProperties);
        
        // Calculate property counts per city
        const counts = transformedProperties.reduce((acc, prop) => {
          const city = prop.city || 'other';
          acc[city] = (acc[city] || 0) + 1;
          acc['all'] = (acc['all'] || 0) + 1;
          return acc;
        }, {});
        setCityCounts(counts);
      } catch (error) {
        console.error('Error fetching properties:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProperties();
  }, []);

  useEffect(() => {
    if (selectedCity === 'all') {
      setFilteredProperties(properties);
    } else {
      const filtered = properties.filter(prop => {
        return prop.city === selectedCity;
      });
      setFilteredProperties(filtered);
    }
  }, [selectedCity, properties]);

  const extractCity = (location) => {
    const cityName = location.split(',')[0].toLowerCase().trim();
    if (cityName.includes('delhi')) return 'delhi';
    if (cityName.includes('gurgaon') || cityName.includes('gurugram')) return 'gurgaon';
    if (cityName.includes('bangalore') || cityName.includes('bengaluru')) return 'bangalore';
    if (cityName.includes('noida')) return 'noida';
    if (cityName.includes('mumbai')) return 'mumbai';
    return 'other';
  };

  const handleCitySelect = (cityId) => {
    setSelectedCity(cityId);
    if (cityId === 'all') {
      searchParams.delete('city');
    } else {
      searchParams.set('city', cityId);
    }
    setSearchParams(searchParams);
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-32 flex items-center justify-center">
        <div className="text-white/60">Loading properties...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-32 pb-24">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-12"
        >
          <h1 className="text-4xl font-light mb-4" data-testid="properties-title">Investment Properties</h1>
          <p className="text-white/60">Select your preferred city to explore premium real estate opportunities</p>
        </motion.div>

        {/* City Selector */}
        <div className="mb-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              {CITIES.map((city, index) => {
                const isSelected = selectedCity === city.id;
                const count = cityCounts[city.id] || 0;
                
                return (
                  <motion.button
                    key={city.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                    onClick={() => handleCitySelect(city.id)}
                    className={`relative p-6 rounded-xl border transition-all duration-300 ${
                      isSelected 
                        ? 'bg-[#4ADE80]/10 border-[#4ADE80]' 
                        : 'bg-[#0A0A0A] border-white/5 hover:border-white/20'
                    }`}
                    data-testid={`city-${city.id}`}
                  >
                    <h3 className={`text-lg font-medium mb-2 transition-colors duration-300 ${
                      isSelected ? 'text-[#4ADE80]' : 'text-white'
                    }`}>
                      {city.name}
                    </h3>
                    <p className="text-sm text-white/40">
                      {count} {count === 1 ? 'property' : 'properties'}
                    </p>
                    {isSelected && (
                      <motion.div
                        layoutId="selected-city-indicator"
                        className="absolute inset-0 border-2 border-[#4ADE80] rounded-xl pointer-events-none"
                        initial={false}
                        transition={{ 
                          type: "spring", 
                          stiffness: 500, 
                          damping: 40,
                          mass: 0.8
                        }}
                        style={{
                          boxShadow: '0 0 20px rgba(74, 222, 128, 0.3)'
                        }}
                      />
                    )}
                  </motion.button>
                );
              })}
            </div>
          </motion.div>
        </div>

        {/* Selected City Info */}
        {selectedCity !== 'all' && filteredProperties.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8 p-6 bg-[#0A0A0A] border border-white/5 rounded-xl"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <MapPin className="w-5 h-5 text-[#4ADE80]" />
                <div>
                  <h3 className="font-medium">
                    {CITIES.find(c => c.id === selectedCity)?.name} Properties
                  </h3>
                  <p className="text-sm text-white/60">
                    {filteredProperties.length} investment {filteredProperties.length === 1 ? 'opportunity' : 'opportunities'} available
                  </p>
                </div>
              </div>
              <Button
                onClick={() => handleCitySelect('all')}
                variant="ghost"
                className="text-white/60 hover:text-white"
              >
                View All Cities
              </Button>
            </div>
          </motion.div>
        )}

        {/* Properties Grid */}
        {filteredProperties.length === 0 ? (
          <div className="text-center py-24" data-testid="no-properties">
            <Building2 className="w-16 h-16 text-white/20 mx-auto mb-4" />
            <p className="text-white/60 mb-2">
              {selectedCity === 'all' 
                ? 'No properties available at the moment' 
                : `No properties available in ${CITIES.find(c => c.id === selectedCity)?.name}`}
            </p>
            <p className="text-sm text-white/40 mb-6">Check back soon for new investment opportunities</p>
            {selectedCity !== 'all' && (
              <Button
                onClick={() => handleCitySelect('all')}
                className="bg-white text-black hover:bg-gray-200 font-medium rounded-full"
              >
                View All Cities
              </Button>
            )}
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredProperties.map((property, index) => (
              <motion.div
                key={property.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                onClick={() => navigate(`/properties/${property.id}`)}
                className="property-card bg-[#0A0A0A] border border-white/5 rounded-xl overflow-hidden cursor-pointer"
                data-testid={`property-card-${index}`}
              >
                <div className="relative h-56 overflow-hidden">
                  <img 
                    src={property.image_url} 
                    alt={property.title}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute top-4 left-4 px-3 py-1 bg-black/60 backdrop-blur-md text-white text-xs font-medium rounded-full border border-white/10">
                    {property.property_type}
                  </div>
                  <div className="absolute top-4 right-4 px-3 py-1 bg-[#4ADE80] text-black text-xs font-medium rounded-full flex items-center space-x-1">
                    <TrendingUp className="w-3 h-3" />
                    <span>{property.annual_return}% APY</span>
                  </div>
                  {property.is_off_plan && (
                    <div className="absolute bottom-4 left-4 right-4">
                      <div className="glass rounded-lg p-3">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-xs text-white/60">Construction Progress</span>
                          <span className="text-xs text-[#4ADE80] font-mono">{property.construction_progress}%</span>
                        </div>
                        <div className="w-full h-1.5 bg-white/10 rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-[#4ADE80] transition-all duration-500"
                            style={{ width: `${property.construction_progress}%` }}
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-medium mb-2">{property.title}</h3>
                  <div className="flex items-center space-x-2 text-sm text-white/40 mb-6">
                    <MapPin className="w-4 h-4" />
                    <span>{property.location}</span>
                  </div>
                  
                  <div className="space-y-4 mb-6">
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-white/40">Total Value</span>
                      <span className="text-sm font-mono">{formatCurrencyShort(property.total_value)}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-white/40">Price per Share</span>
                      <span className="text-sm font-mono">{formatCurrency(property.price_per_share)}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-white/40">Available Shares</span>
                      <span className="text-sm font-mono">{property.available_shares.toFixed(2)}</span>
                    </div>
                  </div>

                  <div className="w-full bg-white/5 rounded-full h-2 mb-2">
                    <div 
                      className="bg-[#4ADE80] h-2 rounded-full transition-all duration-500"
                      style={{ width: `${((property.total_shares - property.available_shares) / property.total_shares) * 100}%` }}
                    ></div>
                  </div>
                  <p className="text-xs text-white/40 mb-4">
                    {((property.total_shares - property.available_shares) / property.total_shares * 100).toFixed(1)}% funded
                  </p>

                  <Button 
                    className="w-full bg-white text-black hover:bg-gray-200 font-medium rounded-full"
                    data-testid={`view-property-button-${index}`}
                  >
                    View Details
                  </Button>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}